import { base44 } from '@/api/base44Client';

// ============================================
// SESSION & USER IDENTIFICATION
// ============================================

// Generate or get session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('flow_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
    sessionStorage.setItem('flow_session_id', sessionId);
    sessionStorage.setItem('flow_session_start', Date.now().toString());
  }
  return sessionId;
};

// Generate anonymous user ID (persists across sessions)
const getUserId = () => {
  let userId = localStorage.getItem('flow_user_id');
  if (!userId) {
    userId = 'user_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 11);
    localStorage.setItem('flow_user_id', userId);
  }
  return userId;
};

// Get B44 click ID if present (for conversion matching)
// Reads from URL param, then session, then the checkoutRedirect storage (enkel-tracking-params)
const getB44ClickId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const b44 = urlParams.get('b44');
  if (b44) {
    sessionStorage.setItem('b44_click_id', b44);
    return b44;
  }
  const fromSession = sessionStorage.getItem('b44_click_id');
  if (fromSession) return fromSession;
  // Fall back to the checkoutRedirect storage key
  try {
    const trackingData = JSON.parse(sessionStorage.getItem('enkel-tracking-params') || localStorage.getItem('enkel-tracking-params') || '{}');
    if (trackingData.b44_click_id) return trackingData.b44_click_id;
  } catch {}
  return '';
};

// ============================================
// DEVICE & BROWSER DETECTION
// ============================================

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
};

const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Other';
};

const getOS = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Other';
};

// ============================================
// ATTRIBUTION & SOURCE TRACKING
// ============================================

// Get all attribution parameters
const getAttributionParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  
  // First touch attribution (stored on first visit)
  const firstTouch = JSON.parse(localStorage.getItem('first_touch_attribution') || '{}');
  
  // Current/last touch attribution
  const current = {
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_content: urlParams.get('utm_content') || '',
    utm_term: urlParams.get('utm_term') || '',
    gclid: urlParams.get('gclid') || '',
    gbraid: urlParams.get('gbraid') || '',
    wbraid: urlParams.get('wbraid') || '',
    fbclid: urlParams.get('fbclid') || '',
    msclkid: urlParams.get('msclkid') || '',
    ttclid: urlParams.get('ttclid') || '',
    affiliate_click_id: urlParams.get('affiliate_click_id') || urlParams.get('clickid') || '',
    b44_click_id: getB44ClickId()
  };
  
  // Session attribution (persisted for session)
  const session = {
    utm_source: current.utm_source || sessionStorage.getItem('utm_source') || '',
    utm_medium: current.utm_medium || sessionStorage.getItem('utm_medium') || '',
    utm_campaign: current.utm_campaign || sessionStorage.getItem('utm_campaign') || '',
    utm_content: current.utm_content || sessionStorage.getItem('utm_content') || '',
    utm_term: current.utm_term || sessionStorage.getItem('utm_term') || '',
    gclid: current.gclid || sessionStorage.getItem('gclid') || '',
    gbraid: current.gbraid || sessionStorage.getItem('gbraid') || '',
    wbraid: current.wbraid || sessionStorage.getItem('wbraid') || '',
    fbclid: current.fbclid || sessionStorage.getItem('fbclid') || '',
    msclkid: current.msclkid || sessionStorage.getItem('msclkid') || '',
    ttclid: current.ttclid || sessionStorage.getItem('ttclid') || '',
    affiliate_click_id: current.affiliate_click_id || sessionStorage.getItem('affiliate_click_id') || '',
    b44_click_id: current.b44_click_id
  };
  
  return { firstTouch, session, current };
};

// Derive source from click IDs when utm_source is missing
const deriveSource = (params) => {
  if (params.utm_source) return params.utm_source;
  if (params.gclid || params.gbraid || params.wbraid) return 'google';
  if (params.fbclid) return 'facebook';
  if (params.msclkid) return 'bing';
  if (params.ttclid) return 'tiktok';
  
  // Check referrer
  const ref = document.referrer.toLowerCase();
  if (ref.includes('readpeak')) return 'readpeak';
  if (ref.includes('google.')) return 'google_organic';
  if (ref.includes('facebook.') || ref.includes('fb.')) return 'facebook_organic';
  if (ref.includes('instagram.')) return 'instagram_organic';
  if (ref.includes('linkedin.')) return 'linkedin_organic';
  if (ref.includes('twitter.') || ref.includes('x.com')) return 'twitter_organic';
  if (ref.includes('bing.') || ref.includes('msn.')) return 'bing_organic';
  if (ref.includes('tiktok.')) return 'tiktok_organic';
  if (ref && !ref.includes('enkelenergi.dk') && !ref.includes('base44')) return 'referral';
  
  return 'direct';
};

// Derive medium from source and params
const deriveMedium = (params, source) => {
  if (params.utm_medium) return params.utm_medium;
  if (params.gclid || params.gbraid || params.wbraid) return 'cpc';
  if (params.msclkid) return 'cpc';
  if (params.fbclid) return 'paid_social';
  if (params.ttclid) return 'paid_social';
  if (source === 'readpeak') return 'native';
  if (source.includes('_organic')) return 'organic';
  if (['facebook_organic','instagram_organic','linkedin_organic','twitter_organic','tiktok_organic'].some(s => source === s)) return 'social';
  if (source === 'referral') return 'referral';
  return 'none';
};

// Store attribution on first visit and update session
const storeAttribution = () => {
  const urlParams = new URLSearchParams(window.location.search);
  
  // Store current params in session
  const paramsToStore = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'affiliate_click_id'];
  paramsToStore.forEach(param => {
    const value = urlParams.get(param);
    if (value) sessionStorage.setItem(param, value);
  });
  
  // First touch attribution (only set once)
  if (!localStorage.getItem('first_touch_attribution')) {
    const firstTouch = {
      utm_source: urlParams.get('utm_source') || deriveSource({}),
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      referrer: document.referrer || '',
      landing_page: window.location.pathname,
      timestamp: new Date().toISOString()
    };
    safeLocalStorageSet('first_touch_attribution', JSON.stringify(firstTouch));
  }
  
  // Landing page for this session
  if (!sessionStorage.getItem('session_landing_page')) {
    sessionStorage.setItem('session_landing_page', window.location.pathname);
    sessionStorage.setItem('session_landing_url', window.location.href);
    sessionStorage.setItem('session_referrer', document.referrer);
  }
};

// Safe localStorage setter — clears stale keys on QuotaExceededError then retries
const safeLocalStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    if (e instanceof DOMException && (e.name === 'QuotaExceededError' || e.code === 22)) {
      const ownedPrefixes = ['flow_', 'first_touch', 'enkel-'];
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && ownedPrefixes.some(p => k.startsWith(p)) && k !== key) toRemove.push(k);
      }
      toRemove.forEach(k => localStorage.removeItem(k));
      try { localStorage.setItem(key, value); } catch {}
    }
  }
};

// Initialize on load
storeAttribution();

// ============================================
// CLIENT IP CAPTURE (once per session)
// ============================================
// The IP is attached to FlowAnalytics events so conversions recorded on the
// external checkout (which logs SaleEvent.ip_address) can be fuzzy-matched
// back to browsing sessions even when no click ID survives the redirect.

// IP + geo are cached in localStorage for 12h so returning sessions have the IP
// available on their very FIRST event (household/IP journey stitching depends on
// it). On a cold cache the first event awaits the lookup briefly (capped) so the
// landing pageview — often the only event of an ad visit — still carries the IP.
let ipFetchPromise = null;
const fetchClientIp = () => {
  try {
    const cached = JSON.parse(localStorage.getItem('flow_client_ip_v2') || 'null');
    if (cached?.ip && Date.now() - cached.ts < 12 * 3600000) {
      sessionStorage.setItem('flow_client_ip', cached.ip);
      if (cached.geo?.country) sessionStorage.setItem('flow_client_geo', JSON.stringify(cached.geo));
      return Promise.resolve();
    }
    if (sessionStorage.getItem('flow_client_ip')) return Promise.resolve();
    if (ipFetchPromise) return ipFetchPromise;
    ipFetchPromise = base44.functions.invoke('getClientIp', {}).then(res => {
      const ip = res?.data?.ip || '';
      const { country = '', region = '', city = '' } = res?.data || {};
      const geo = { country, region, city };
      if (ip) {
        sessionStorage.setItem('flow_client_ip', ip);
        safeLocalStorageSet('flow_client_ip_v2', JSON.stringify({ ip, geo, ts: Date.now() }));
      }
      if (country) sessionStorage.setItem('flow_client_geo', JSON.stringify(geo));
    }).catch(() => {});
    return ipFetchPromise;
  } catch { return Promise.resolve(); }
};

// ============================================
// PAGE INTERACTION TRACKING
// ============================================

let pageStartTime = Date.now();
let maxScrollDepth = 0;
let interactionCount = 0;
let clickTargets = [];
let lastActivityTime = Date.now();

// Track scroll depth
if (typeof window !== 'undefined') {
  let scrollFrame = null;
  window.addEventListener('scroll', () => {
    lastActivityTime = Date.now();
    if (scrollFrame !== null) return;
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = null;
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      maxScrollDepth = Math.max(maxScrollDepth, Math.min(100, scrollPercent));
    });
  }, { passive: true });

  // Track clicks with target info
  window.addEventListener('click', (e) => {
    interactionCount++;
    lastActivityTime = Date.now();
    
    // Capture click target for funnel analysis
    const target = e.target.closest('button, a, [role="button"]');
    if (target) {
      const info = {
        tag: target.tagName,
        text: (target.textContent || '').slice(0, 50).trim(),
        href: target.href || '',
        id: target.id || '',
        className: (target.className || '').toString().slice(0, 100)
      };
      clickTargets.push(info);
      if (clickTargets.length > 20) clickTargets.shift(); // Keep last 20
    }
  }, { passive: true });

  // Track form inputs
  window.addEventListener('input', () => {
    interactionCount++;
    lastActivityTime = Date.now();
  }, { passive: true });
}

// ============================================
// INTERNAL TRAFFIC DETECTION
// ============================================

const isInternalTraffic = () => {
  const hostname = window.location.hostname || '';
  const referrer = document.referrer || '';
  const pagePath = (window.location.pathname || '').toLowerCase();

  // Base44 preview / builder domains
  if (hostname.includes('base44.app') || hostname.includes('base44.io') || hostname === 'localhost') return true;

  // Referrer from base44 (e.g. clicking links inside the builder)
  if (referrer.includes('base44.app') || referrer.includes('base44.io')) return true;

  // Internal-only pages that should never be tracked
  const internalPages = ['/internalanalytics', '/dealbuilder-internal', '/media-creator', '/upload'];
  if (internalPages.some(p => pagePath.startsWith(p))) return true;

  // Inside an iframe (base44 preview)
  try {
    if (window.self !== window.top && document.referrer.includes('base44')) return true;
  } catch (e) {
    // Cross-origin iframe — likely base44 preview
    return true;
  }

  return false;
};

// ============================================
// MAIN TRACKING FUNCTION
// ============================================

export const trackFlowEvent = async (eventType, data = {}) => {
  try {
    // Skip tracking for internal/builder traffic
    if (isInternalTraffic()) return null;
    // Wait briefly (max 1.5s) for the IP on a cold cache so even single-page
    // ad visits carry ip_address — callers never await this function, so the
    // small delay is invisible to the UI.
    await Promise.race([fetchClientIp(), new Promise(r => setTimeout(r, 1500))]);
    const attribution = getAttributionParams();
    const sessionAttribution = attribution.session;
    const derivedSource = deriveSource(sessionAttribution);
    const derivedMedium = deriveMedium(sessionAttribution, derivedSource);
    const timeOnPage = Math.round((Date.now() - pageStartTime) / 1000);
    const sessionDuration = Math.round((Date.now() - parseInt(sessionStorage.getItem('flow_session_start') || Date.now())) / 1000);
    
    const eventData = {
      // Identity
      session_id: getSessionId(),
      user_id: getUserId(),
      b44_click_id: sessionAttribution.b44_click_id,
      
      // Event info
      event_type: eventType,
      page_path: data.pagePath || window.location.pathname,
      page_name: data.pageName || '',
      page_url: window.location.href,
      step_name: data.stepName || '',
      step_number: data.stepNumber || null,
      flow_name: data.flowName || '',
      flow_entry_point: data.entryPoint || sessionStorage.getItem('flow_entry_point') || '',
      
      // Attribution - Session (last touch)
      utm_source: sessionAttribution.utm_source || derivedSource,
      utm_medium: sessionAttribution.utm_medium || derivedMedium,
      utm_campaign: sessionAttribution.utm_campaign,
      utm_content: sessionAttribution.utm_content,
      utm_term: sessionAttribution.utm_term,
      gclid: sessionAttribution.gclid,
      gbraid: sessionAttribution.gbraid,
      wbraid: sessionAttribution.wbraid,
      fbclid: sessionAttribution.fbclid,
      msclkid: sessionAttribution.msclkid,
      ttclid: sessionAttribution.ttclid,
      affiliate_click_id: sessionAttribution.affiliate_click_id,
      
      // Derived attribution
      source_derived: derivedSource,
      medium_derived: derivedMedium,
      
      // Referrer info
      referrer: document.referrer || '',
      referrer_domain: document.referrer ? new URL(document.referrer).hostname : '',
      landing_page: sessionStorage.getItem('session_landing_page') || '',
      landing_url: sessionStorage.getItem('session_landing_url') || '',
      
      // Device info
      device_type: getDeviceType(),
      browser: getBrowser(),
      os: getOS(),
      ip_address: sessionStorage.getItem('flow_client_ip') || '',
      ...(() => {
        try {
          const g = JSON.parse(sessionStorage.getItem('flow_client_geo') || '{}');
          return g.country ? { geo_country: g.country, geo_region: g.region || '', geo_city: g.city || '' } : {};
        } catch { return {}; }
      })(),
      screen_width: window.screen?.width || 0,
      screen_height: window.screen?.height || 0,
      viewport_width: window.innerWidth || 0,
      viewport_height: window.innerHeight || 0,
      
      // Timing
      timestamp: new Date().toISOString(),
      time_on_page: timeOnPage,
      session_duration: sessionDuration,
      
      // Engagement
      scroll_depth: Math.round(maxScrollDepth),
      interaction_count: interactionCount,
      last_click_targets: clickTargets.slice(-5), // Last 5 clicks
      
      // Metadata
      metadata: {
        ...data.metadata,
        page_title: document.title || ''
      }
    };

    // Fire and forget - don't block the UI
    base44.entities.FlowAnalytics.create(eventData).catch(err => {
      // Flow tracking failed
    });

    return eventData;
  } catch (error) {
    // Flow tracking error
    return null;
  }
};

// ============================================
// CONVENIENCE METHODS
// ============================================

export const trackPageView = (pageName, pagePath) => {
  // Reset page metrics for new page
  pageStartTime = Date.now();
  maxScrollDepth = 0;
  interactionCount = 0;
  clickTargets = [];
  
  return trackFlowEvent('page_view', { pageName, pagePath });
};

export const trackFlowStep = (flowName, stepName, stepNumber, metadata = {}) => {
  return trackFlowEvent('step_complete', { flowName, stepName, stepNumber, metadata });
};

export const trackWidgetInteraction = (widgetName, action, metadata = {}) => {
  return trackFlowEvent('widget_interaction', { 
    flowName: widgetName, 
    stepName: action,
    metadata 
  });
};

export const trackFormStart = (formName) => {
  return trackFlowEvent('form_start', { flowName: formName });
};

export const trackFormComplete = (formName, metadata = {}) => {
  return trackFlowEvent('form_complete', { flowName: formName, metadata });
};

export const trackCheckoutStart = (metadata = {}) => {
  return trackFlowEvent('checkout_start', { metadata });
};

export const trackCheckoutComplete = (metadata = {}) => {
  return trackFlowEvent('checkout_complete', { metadata });
};

export const trackDropOff = (flowName, stepName, reason = '') => {
  return trackFlowEvent('drop_off', { flowName, stepName, metadata: { reason } });
};

export const trackCTAClick = (ctaName, destination, metadata = {}) => {
  return trackFlowEvent('cta_click', { 
    stepName: ctaName, 
    metadata: { destination, ...metadata } 
  });
};

export const trackExternalRedirect = (destination, metadata = {}) => {
  return trackFlowEvent('external_redirect', { 
    stepName: 'checkout_redirect',
    metadata: { destination, ...metadata }
  });
};

export const setEntryPoint = (entryPoint) => {
  sessionStorage.setItem('flow_entry_point', entryPoint);
};

// Get current session data for debugging/conversion matching
export const getSessionData = () => {
  return {
    sessionId: getSessionId(),
    userId: getUserId(),
    b44ClickId: getB44ClickId(),
    attribution: getAttributionParams(),
    landingPage: sessionStorage.getItem('session_landing_page'),
    referrer: sessionStorage.getItem('session_referrer'),
    sessionStart: sessionStorage.getItem('flow_session_start')
  };
};

// ============================================
// EXPORTS
// ============================================

export const FlowTracker = {
  trackPageView,
  trackFlowStep,
  trackWidgetInteraction,
  trackFormStart,
  trackFormComplete,
  trackCheckoutStart,
  trackCheckoutComplete,
  trackDropOff,
  trackCTAClick,
  trackExternalRedirect,
  setEntryPoint,
  getSessionId,
  getUserId,
  getB44ClickId,
  getSessionData,
  getAttributionParams,
  deriveSource,
  deriveMedium
};

export default FlowTracker;